// Integration point for search quality assessment.
//
// Builds a JSON "Search Quality Report" comparing the displayed Algolia results
// against both local engines (Fuse + MiniSearch) for the current query, and
// emits it to the CDP analytics platform (plus the dev console). Algolia is
// treated as the gold standard for the content indices; non-content registries
// (navigation / external-help / quick-actions) are compared Fuse-vs-MiniSearch
// only (no gold standard, so no score).

import { getDocumentType } from '../../utils/search/getDocumentType'
import { getQuestionBodyLabel } from '../../utils/getQuestionBodyLabel'
import { localProviderId } from './presentationEngine'
import analytics from '../../utils/cdpAnalytics'

/**
 * Report a comparison between the displayed provider's results and a shadow
 * provider's results for the same query. Called by ShadowProvider whenever a
 * shadow provider returns results.
 *
 * @param {Object} report
 * @param {string} report.query                 - The search query
 * @param {string} report.displayedProvider     - ID of the provider whose results are shown
 * @param {Object} report.displayedResults      - Full results shape from the displayed provider
 * @param {string} report.shadowProvider        - ID of the shadow provider
 * @param {Object} report.shadowResults         - Full results shape from the shadow provider
 */
export function reportSearchComparison({
  query,
  displayedProvider,
  displayedResults,
  shadowProvider,
  shadowResults,
}) {
  // eslint-disable-next-line no-console
  console.log('[searchReporter] comparison', {
    query,
    displayedProvider,
    displayedResults,
    shadowProvider,
    shadowResults,
  })
}

const CONTENT_INDEXES = ['repos', 'folders', 'sets_questions']
const NON_CONTENT_SOURCES = ['navigation', 'external-help', 'quick-actions']
const IS_DEV = process.env.NODE_ENV !== 'production'

// Round to 4 decimals; pass through non-finite/non-number values unchanged.
function round(n) {
  if (typeof n !== 'number' || !Number.isFinite(n)) return n
  return Number(n.toFixed(4))
}

// Pearson correlation of two equal-length numeric arrays. Returns null when
// undefined (fewer than 2 points, or zero variance on either side).
function pearson(xs, ys) {
  const n = xs.length
  if (n < 2) return null
  const mean = (arr) => arr.reduce((acc, v) => acc + v, 0) / arr.length
  const mx = mean(xs)
  const my = mean(ys)
  let num = 0
  let dx = 0
  let dy = 0
  for (let i = 0; i < n; i += 1) {
    const a = xs[i] - mx
    const b = ys[i] - my
    num += a * b
    dx += a * a
    dy += b * b
  }
  if (dx === 0 || dy === 0) return null
  return num / Math.sqrt(dx * dy)
}

// Spearman rank correlation over the objectIDs shared by two ordered id lists,
// approximated as the Pearson correlation of their positions. Returns null when
// fewer than 2 ids are shared.
function rankCorrelation(orderA, orderB) {
  const idxB = new Map(orderB.map((id, i) => [id, i]))
  const shared = orderA.filter((id) => idxB.has(id))
  if (shared.length < 2) return null
  const idxA = new Map(orderA.map((id, i) => [id, i]))
  const xs = shared.map((id) => idxA.get(id))
  const ys = shared.map((id) => idxB.get(id))
  return pearson(xs, ys)
}

// Identify a hit for the excess/missing lists.
function describeHit(hit) {
  const documentType = getDocumentType(hit)
  const name = documentType === 'question' ? getQuestionBodyLabel(hit) : hit.name
  return { objectID: hit.objectID, name: name || '', documentType }
}

function objectIds(hits) {
  return (Array.isArray(hits) ? hits : []).map((h) => h.objectID)
}

function getBlock(additiveResults, providerId) {
  if (!Array.isArray(additiveResults)) return null
  return additiveResults.find((r) => r.providerId === providerId) || null
}

function blockIndexHits(block, indexName) {
  const ir = block && block.indexResults
  const slot = ir && ir[indexName]
  return (slot && Array.isArray(slot.hits)) ? slot.hits : []
}

function getFlatHits(additiveResults, providerId) {
  const block = getBlock(additiveResults, providerId)
  return (block && Array.isArray(block.hits)) ? block.hits : []
}

// Compare one engine's hits for an index against the Algolia gold standard.
//
// Algolia is treated as the "correct" result set (the gold standard). A local
// engine (Fuse or MiniSearch) is scored on how closely its results match
// Algolia's, both in membership (which objectIDs are returned) and in ordering.
// Let `overlap` = number of objectIDs the engine returned that Algolia also
// returned.
//
//   precision = overlap / (engine result count)
//       Of everything the engine returned, what fraction did Algolia also
//       return? Low precision ⇒ the engine surfaces extra results Algolia did
//       not (potential false positives / noise).
//
//   recall = overlap / (algolia result count)
//       Of everything Algolia returned, what fraction did the engine also
//       return? Low recall ⇒ the engine misses results Algolia found
//       (potential false negatives / gaps).
//
//   qualityScore = F1 = 2 * precision * recall / (precision + recall)
//       The harmonic mean of precision and recall — a single 0..1 membership
//       score that is only high when BOTH precision and recall are high. 1.0
//       means the engine returned exactly Algolia's set; 0 means no overlap.
//
//   rankCorrelation = Spearman rank correlation over the shared objectIDs
//       (approximated as the Pearson correlation of their positions in each
//       ordered list). Measures ORDERING agreement, independent of membership:
//       +1 ⇒ the engine ranks the shared results in the same order as Algolia,
//        0 ⇒ no ordering relationship, −1 ⇒ reversed order. null when fewer
//       than two results are shared (ordering is undefined).
//
// precision/recall/qualityScore answer "did we return the right items?";
// rankCorrelation answers "did we put them in the right order?".
function engineIndexMetric(algoliaHits, engineHits) {
  const algoliaIds = objectIds(algoliaHits)
  const engineIds = objectIds(engineHits)
  const algoliaSet = new Set(algoliaIds)
  const engineSet = new Set(engineIds)

  const excessHits = engineHits.filter((h) => !algoliaSet.has(h.objectID))
  const missingHits = algoliaHits.filter((h) => !engineSet.has(h.objectID))
  const overlap = engineIds.length - excessHits.length

  const precision = engineIds.length ? overlap / engineIds.length : 0
  const recall = algoliaIds.length ? overlap / algoliaIds.length : 0
  const f1 = (precision + recall) ? (2 * precision * recall) / (precision + recall) : 0
  const rho = rankCorrelation(algoliaIds, engineIds)

  return {
    excess: excessHits.length,
    missing: missingHits.length,
    excessHits: excessHits.map(describeHit),
    missingHits: missingHits.map(describeHit),
    precision: round(precision),
    recall: round(recall),
    rankCorrelation: rho == null ? null : round(rho),
    qualityScore: round(f1),
  }
}

function buildIndexMetric(algoliaHits, fuseHits, miniHits) {
  const algoliaSet = new Set(objectIds(algoliaHits))
  const fuseSet = new Set(objectIds(fuseHits))
  const countShared = (set, hits) => hits.filter((h) => set.has(h.objectID)).length
  return {
    hits: {
      algolia: algoliaHits.length,
      fuse: fuseHits.length,
      minisearch: miniHits.length,
    },
    matching: {
      algoliaFuse: countShared(algoliaSet, fuseHits),
      algoliaMinisearch: countShared(algoliaSet, miniHits),
      fuseMinisearch: countShared(fuseSet, miniHits),
    },
    fuse: engineIndexMetric(algoliaHits, fuseHits),
    minisearch: engineIndexMetric(algoliaHits, miniHits),
  }
}

// Algolia-hit-count-weighted average of an engine's per-index scores.
function weightedOverall(indices, engine) {
  const keys = Object.keys(indices)
  const totalWeight = keys.reduce((acc, k) => acc + indices[k].hits.algolia, 0)
  const divisor = totalWeight || 1
  let precision = 0
  let recall = 0
  let qualityScore = 0
  let rho = 0
  let rhoWeight = 0
  keys.forEach((k) => {
    const metric = indices[k][engine]
    const weight = indices[k].hits.algolia
    precision += metric.precision * weight
    recall += metric.recall * weight
    qualityScore += metric.qualityScore * weight
    if (metric.rankCorrelation != null) {
      rho += metric.rankCorrelation * weight
      rhoWeight += weight
    }
  })
  return {
    precision: round(precision / divisor),
    recall: round(recall / divisor),
    qualityScore: round(qualityScore / divisor),
    rankCorrelation: rhoWeight ? round(rho / rhoWeight) : null,
  }
}

function buildContentReport(displayedResults, shadowResults) {
  const algoliaIndexResults = (displayedResults && displayedResults.indexResults) || {}
  const fuseBlock = getBlock(shadowResults, localProviderId('content', 'fuse'))
  const miniBlock = getBlock(shadowResults, localProviderId('content', 'minisearch'))

  const indices = {}
  CONTENT_INDEXES.forEach((indexName) => {
    const algoliaHits = (algoliaIndexResults[indexName] && algoliaIndexResults[indexName].hits) || []
    const fuseHits = blockIndexHits(fuseBlock, indexName)
    const miniHits = blockIndexHits(miniBlock, indexName)
    indices[indexName] = buildIndexMetric(algoliaHits, fuseHits, miniHits)
  })

  const keys = Object.keys(indices)
  const sum = (selector) => keys.reduce((acc, k) => acc + selector(indices[k]), 0)
  const overall = {
    hits: {
      algolia: sum((m) => m.hits.algolia),
      fuse: sum((m) => m.hits.fuse),
      minisearch: sum((m) => m.hits.minisearch),
    },
    fuse: weightedOverall(indices, 'fuse'),
    minisearch: weightedOverall(indices, 'minisearch'),
  }

  return { indices, overall }
}

// Non-content registries: Fuse vs MiniSearch only (no gold standard → no score).
function buildNonContentReport(additiveResults) {
  const out = {}
  NON_CONTENT_SOURCES.forEach((base) => {
    const fuseHits = getFlatHits(additiveResults, localProviderId(base, 'fuse'))
    const miniHits = getFlatHits(additiveResults, localProviderId(base, 'minisearch'))
    const fuseSet = new Set(objectIds(fuseHits))
    const miniSet = new Set(objectIds(miniHits))
    out[base] = {
      hits: { fuse: fuseHits.length, minisearch: miniHits.length },
      matching: { fuseMinisearch: fuseHits.filter((h) => miniSet.has(h.objectID)).length },
      fuseOnly: fuseHits
        .filter((h) => !miniSet.has(h.objectID))
        .map((h) => ({ objectID: h.objectID, label: h.label })),
      minisearchOnly: miniHits
        .filter((h) => !fuseSet.has(h.objectID))
        .map((h) => ({ objectID: h.objectID, label: h.label })),
    }
  })
  return out
}

/**
 * Build the full JSON search-quality report for the current query.
 *
 * @param {Object} input
 * @param {string} input.query
 * @param {string} input.displayedProvider
 * @param {Object} input.displayedResults     Algolia display results (SearchContext shape)
 * @param {Array}  input.shadowResults        Shadow provider results (Fuse/MiniSearch content)
 * @param {Array}  input.additiveResults      Additive provider results (non-content registries)
 * @returns {Object}
 */
export function buildSearchQualityReport({
  query,
  displayedProvider,
  displayedResults,
  shadowResults,
  additiveResults,
}) {
  const content = buildContentReport(displayedResults, shadowResults)
  const overallScores = [content.overall.fuse.qualityScore, content.overall.minisearch.qualityScore]
    .filter((v) => typeof v === 'number')
  const score = {
    fuse: content.overall.fuse,
    minisearch: content.overall.minisearch,
    overall: overallScores.length ?
      round(overallScores.reduce((acc, v) => acc + v, 0) / overallScores.length) :
      null,
  }
  return {
    query,
    displayedProvider: displayedProvider || 'algolia',
    content,
    score,
    nonContent: buildNonContentReport(additiveResults),
  }
}

/**
 * Build the search-quality report and ship it to the CDP analytics platform.
 * Also logs the JSON to the console in development for live inspection.
 *
 * @param {Object} report
 * @param {string} report.query
 * @param {string} report.displayedProvider
 * @param {Object} report.displayedResults
 * @param {Array}  report.shadowResults
 * @param {Array}  report.additiveResults
 */
export function reportLocalVsAlgolia({
  query,
  displayedProvider,
  displayedResults,
  shadowResults,
  additiveResults,
}) {
  if (!query) return

  const report = buildSearchQualityReport({
    query,
    displayedProvider,
    displayedResults,
    shadowResults,
    additiveResults,
  })

  // Flatten the headline scores into top-level scalar attributes so the CDP
  // ingest exposes them as queryable columns (nested objects are only stored as
  // jsonb). The full composed report is sent alongside for deep inspection.
  const { score } = report
  analytics.track('NewSearch Quality Report', {
    query,
    displayedProvider: report.displayedProvider,
    scoreOverall: score.overall,
    fuseQualityScore: score.fuse.qualityScore,
    fusePrecision: score.fuse.precision,
    fuseRecall: score.fuse.recall,
    fuseRankCorrelation: score.fuse.rankCorrelation,
    minisearchQualityScore: score.minisearch.qualityScore,
    minisearchPrecision: score.minisearch.precision,
    minisearchRecall: score.minisearch.recall,
    minisearchRankCorrelation: score.minisearch.rankCorrelation,
    report,
  })

  if (IS_DEV) {
    // eslint-disable-next-line no-console
    console.log('[searchReporter] Search Quality Report', report)
  }
}
